import { Client } from './client';
import { TTLSet } from './ttl-set';
export var NudgeCTABlockActionType;
(function (NudgeCTABlockActionType) {
    NudgeCTABlockActionType["OpenUrl"] = "openUrl";
    NudgeCTABlockActionType["CallPhone"] = "callPhone";
    NudgeCTABlockActionType["SendEmail"] = "sendEmail";
    NudgeCTABlockActionType["TriggerEvent"] = "triggerEvent";
})(NudgeCTABlockActionType || (NudgeCTABlockActionType = {}));
export var NudgeCTABlockType;
(function (NudgeCTABlockType) {
    NudgeCTABlockType["Button"] = "button";
    NudgeCTABlockType["Link"] = "link";
})(NudgeCTABlockType || (NudgeCTABlockType = {}));
export class TrackerSdk {
    options;
    client;
    userId;
    global;
    queue = [];
    knownEvents = ['nudge.show'];
    eventListeners = {};
    // this set is used to prevent showing the same nudge multiple times in a short period
    recentNudgeIds = new TTLSet(1000);
    constructor(options) {
        this.options = options;
        const query = {
            sdk_name: options.sdk || 'node',
            sdk_version: options.sdkVersion || '',
        };
        this.client = new Client({
            baseUrl: options.apiUrl || 'https://analytic.brainfi.sh',
            authToken: options.accessKey,
            query,
        });
        this.setupIncomingPayloadHandler();
    }
    // placeholder for future use
    init() {
        // empty
    }
    ready() {
        this.options.waitForUser = false;
        this.flush();
    }
    setupIncomingPayloadHandler() {
        this.client.on('e', this.handleIncomingPayload.bind(this));
    }
    handleIncomingPayload(payload) {
        switch (payload.type) {
            case 'nudge.show': {
                const { nudgeId } = payload.payload;
                if (this.recentNudgeIds.has(nudgeId)) {
                    return;
                }
                this.recentNudgeIds.add(nudgeId);
                this.dispatchEventListeners('nudge.show', payload.payload);
            }
        }
    }
    dispatchEventListeners(event, payload) {
        this.eventListeners[event]?.forEach((handler) => handler(payload));
    }
    on(event, handler) {
        if (!this.knownEvents.includes(event)) {
            // TODO: log an error?
            return;
        }
        if (!this.eventListeners[event]) {
            this.eventListeners[event] = [];
        }
        this.eventListeners[event].push(handler);
    }
    off(event, handler) {
        if (!this.knownEvents.includes(event)) {
            // TODO: log an error?
            return;
        }
        if (!this.eventListeners[event]) {
            return;
        }
        this.eventListeners[event] = this.eventListeners[event].filter((h) => h !== handler);
        if (this.eventListeners[event].length === 0) {
            delete this.eventListeners[event];
        }
    }
    async send(payload, responseCallback) {
        if (this.options.disabled) {
            return Promise.resolve();
        }
        if (this.options.filter && !this.options.filter(payload)) {
            return Promise.resolve();
        }
        if (this.options.waitForUser && !this.userId) {
            this.queue.push(payload);
            return Promise.resolve();
        }
        return this.client.send('t', payload, responseCallback);
    }
    setGlobalProperties(properties) {
        this.global = {
            ...this.global,
            ...properties,
        };
    }
    async track(name, properties) {
        return this.send({
            type: 'event.track',
            payload: {
                name,
                userId: properties?.userId ?? this.userId,
                properties: {
                    ...(this.global ?? {}),
                    ...(properties ?? {}),
                },
            },
        });
    }
    async identify(payload) {
        if (payload.userId) {
            this.userId = payload.userId;
            this.flush();
        }
        // post identify metadata message to iframe
        const iframe = document.querySelector('#bf-iframe-container .trigger-iframe');
        if (iframe) {
            const sendMessage = () => {
                if (iframe.contentWindow) {
                    iframe.contentWindow.postMessage({
                        type: 'USER_IDENTIFY',
                        payload,
                    }, '*');
                }
            };
            // Try to send immediately if contentWindow exists
            if (iframe.contentWindow) {
                sendMessage();
            }
            // Also attach a load listener to ensure the message is sent
            // This covers the case where the iframe wasn't ready initially
            iframe.addEventListener('load', sendMessage);
        }
        if (Object.keys(payload).length > 1) {
            return this.send({
                type: 'user.identify',
                payload: {
                    ...payload,
                    properties: {
                        ...this.global,
                        ...payload.properties,
                    },
                },
            });
        }
    }
    async record(events, callback) {
        return this.send({
            type: 'event.record',
            payload: {
                events,
                userId: this.userId,
            },
        }, callback);
    }
    async alias(payload) {
        return this.send({
            type: 'user.alias',
            payload,
        });
    }
    async increment(payload) {
        return this.send({
            type: 'user.increment',
            payload,
        });
    }
    async decrement(payload) {
        return this.send({
            type: 'user.decrement',
            payload,
        });
    }
    clear() {
        this.userId = undefined;
        // should we force a session end here?
    }
    flush() {
        this.queue.forEach((item) => {
            this.send({
                ...item,
                // Not sure why ts-expect-error is needed here
                // @ts-expect-error
                payload: {
                    ...item.payload,
                    userId: item.payload.userId ?? this.userId,
                },
            });
        });
        this.queue = [];
    }
}
